/* The randomizer.
   A fresh 0-99 for mixed strategies: read against a hand's frequencies, it names the one
   action to take. One number per spot, so a chart read twice in a row does not silently
   change the answer underneath. Moving to another spot draws again; so does a click, for the
   second time the same spot comes up. The value outlives its button, which is rebuilt with
   the selector row it rides on.

   It can be switched off. Reading a chart through a roll is the view for the table; studying
   how a hand is mixed wants the frequencies themselves, so off there is no number and the
   chart shows every mix as it is stored. See docs/specs/2026-09-18-randomizer-toggle-design.md. */

import type { Spot } from "@preflop/core";
import { saveSetting } from "../../state/settings";
import { el } from "../../ui/dom";

/** The setting the choice is kept under, per device until profiles arrive. */
export const RANDOMIZER_SETTING = "randomizer";

// Off until it is asked for: a chart opens on what it holds, the mixes as they are stored.
let on = false;
let randomSpotKey: string | null = null;
let randomRoll: number | null = null; // 0-99, or null before the first draw and while off
let group: HTMLElement | null = null;
let toggle: HTMLButtonElement | null = null;
let button: HTMLButtonElement | null = null;

const randomText = (): string => (randomRoll == null ? "--" : String(randomRoll).padStart(2, "0"));

/** The roll the chart is read against. Null before the first draw, and always while the
    randomizer is off: null is what makes the grid show the mixes. */
export const currentRoll = (): number | null => (on ? randomRoll : null);

export const randomizerOn = (): boolean => on;

/** What the saved setting said, handed in at boot before the first chart is drawn. */
export function initRandomizer(saved: boolean): void {
  on = saved;
  if (!on) randomRoll = null;
}

/** The controls on screen, brought in line with the state. */
function sync(): void {
  group?.classList.toggle("off", !on);
  if (toggle) {
    toggle.ariaChecked = on ? "true" : "false";
    toggle.title = on
      ? "On: each hand shows the one action the number names. Click to see the mixes."
      : "Off: each hand shows its mix. Click to read the chart through a random number.";
  }
  if (button) {
    button.textContent = randomText();
    button.disabled = !on;
    button.title = on ? "Random 0-99 — click for another" : "The randomizer is off";
  }
}

function rollRandom(): void {
  randomRoll = Math.floor(Math.random() * 100);
  sync();
}

/** Same chart, same number; another chart, another draw. Off, the spot is still noted, so
    turning it on later does not read as a change of spot. */
export function renderRandom(spot: Pick<Spot, "key">): void {
  if (spot.key === randomSpotKey) return;
  randomSpotKey = spot.key;
  if (on) rollRandom();
}

/** Switch it. On draws at once, so the switch answers visibly and no number from before it
    was turned off comes back; off forgets the number. The chart is the caller's to redraw. */
export function setRandomizer(next: boolean): void {
  if (next === on) return;
  on = next;
  void saveSetting(RANDOMIZER_SETTING, on);
  if (on) rollRandom();
  else {
    randomRoll = null;
    sync();
  }
}

/* Shaped like the selector groups next to it: caption over its controls. A click asks for
   another number, or switches the whole thing, and the grid has to answer either — `onRoll`
   is that redraw. Drawing the chart is left to the caller everywhere else: inside render()
   the draft the chart comes from has not been settled yet when the number is drawn. */
export function randomizerGroup(onRoll: () => void): HTMLElement {
  toggle = el("button", {
    type: "button",
    class: "switch",
    role: "switch",
    ariaLabel: "Randomizer",
    dataset: { testid: "randomizer-toggle" },
    onclick: () => {
      setRandomizer(!on);
      onRoll();
    },
  });
  button = el("button", {
    type: "button",
    class: "btn num",
    dataset: { testid: "randomizer-roll" },
    onclick: () => {
      rollRandom();
      onRoll();
    },
  });
  group = el(
    "div",
    { class: "group randomizer" },
    el("span", {}, "Randomizer"),
    el("div", { class: "dice" }, toggle, button),
  );
  sync();
  return group;
}
